import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { disableSoundSync, enableSoundSync, resumeSoundSync } from "../../actions/soundSyncActions";
import { REPEAT_MS } from "../../sound/codec";
import { selectIsHost } from "../../selectors";
import { showsBanner } from "../../lib/navigation";

const STATUS_TEXT = {
  starting: "Starting sound sync…",
  broadcasting: "Sound sync on: your song and verse changes are broadcast to nearby phones.",
  listening: "Sound sync on: following the host by sound.",
  interrupted: "Sound sync was interrupted (a call, or the app was in the background).",
  denied: "Sound sync needs the microphone; access was refused.",
  unsupported: "Sound sync is not available on this device.",
  error: "Sound sync could not start.",
};

// Two and a half missed repeats: the host is out of range, muted, or gone.
export const STALE_AFTER_MS = 2.5 * REPEAT_MS;

const signalWord = (db) => (db >= 20 ? "strong" : db >= 10 ? "weak" : "none");

const Bars = () => (
  <span className="banner-bars" aria-hidden="true">
    {[8, 16, 12, 19, 7].map((height, index) => (
      <span key={index} style={{ height }} />
    ))}
  </span>
);

/**
 * Shown on the session pages while the device is offline (or while sound sync is on):
 * offers to keep the room in sync through sound, and shows how that is going.
 */
const OfflineBanner = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const code = useSelector((state) => state.session.Code);
  const isOnline = useSelector((state) => state.session.isOnline);
  const isHost = useSelector(selectIsHost);
  const sync = useSelector((state) => state.soundSync);
  const listening = sync.enabled && sync.role === "participant" && sync.status === "listening";
  const [now, setNow] = useState(() => Date.now());

  // while listening the staleness line counts up; a tick a second is enough
  useEffect(() => {
    if (!listening) return undefined;
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [listening]);

  if (!showsBanner(pathname, { hasSession: Boolean(code) })) return null;
  if (isOnline && !sync.enabled && sync.status === "off") return null;

  const offer = isHost ? "You’re offline. Broadcast your song and verse changes by sound?" : "You’re offline. Follow the host by sound?";
  let text = sync.enabled || sync.status !== "off" ? STATUS_TEXT[sync.status] || offer : offer;
  const busy = sync.status === "starting";

  // participants: whether the host's phone reaches this one
  let signal = null;
  if (listening) {
    const since = sync.lastHeard ?? sync.enabledAt;
    const silentFor = since ? Math.max(0, now - since) : 0;
    if (!sync.lastHeard || silentFor > STALE_AFTER_MS) text = "Sound sync on: waiting for the host’s next message.";
    signal =
      silentFor > STALE_AFTER_MS
        ? `Nothing heard from the host for ${Math.round(silentFor / 1000)} s. Move closer and check that the host is broadcasting.`
        : `Nearby tone signal: ${signalWord(sync.signal)}.`;
  }

  return (
    <div className={`banner${isOnline ? "" : " banner-offline"}`} role="status" data-screen-label="Offline banner">
      {sync.enabled && (sync.status === "listening" || sync.status === "broadcasting") && <Bars />}
      <span className="banner-text">
        {isOnline || sync.enabled || sync.status !== "off" ? text : offer}
        {signal && <span className="banner-detail"> {signal}</span>}
        {sync.error && <span className="banner-detail"> {sync.error}</span>}
      </span>
      {sync.enabled && sync.status === "interrupted" && (
        <button type="button" className="btn btn-outline" onClick={() => dispatch(resumeSoundSync())}>
          Resume
        </button>
      )}
      {sync.enabled ? (
        <button type="button" className="btn btn-dark" onClick={() => dispatch(disableSoundSync())}>
          Turn off
        </button>
      ) : (
        <button type="button" className="btn btn-dark" onClick={() => dispatch(enableSoundSync())} disabled={busy}>
          {sync.status === "off" ? "Enable sound sync" : "Try again"}
        </button>
      )}
    </div>
  );
};

export default OfflineBanner;
